import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronRight,
  ChevronLeft,
  X,
  Play,
  Volume2,
  VolumeX,
  HelpCircle,
  ExternalLink,
  Rocket,
  LayoutDashboard,
  Search,
  Zap,
  Settings,
  Sparkles,
} from 'lucide-react';
import { UserContext } from '../context/UserContext';
import { ROLE_GROUPS, hasRole } from '../config/navigation';
import { t } from '../config/onboardingStrings';
import { STEP_MEDIA, HELP_ARTICLES, USER_GOALS, formatTime, getEstimatedTime } from '../config/onboardingMedia';

// Default steps for hotspot tour
const getDefaultSteps = (locale, userRoles) => {
  const isAdmin = hasRole(userRoles, ROLE_GROUPS.ADMIN || []);

  const steps = [
    {
      id: 'welcome',
      title: t(locale, 'welcome.title'),
      description: t(locale, 'welcome.description'),
      icon: Rocket,
    },
    {
      id: 'dashboard',
      title: t(locale, 'dashboard.title'),
      description: t(locale, 'dashboard.description'),
      spotlight: '[data-tour="dashboard-link"]',
      icon: LayoutDashboard,
    },
    {
      id: 'search',
      title: t(locale, 'search.title'),
      description: t(locale, 'search.description'),
      spotlight: '[data-tour="search-button"]',
      interactive: true,
      interactiveAction: 'triggerSearch',
      icon: Search,
    },
    {
      id: 'quick-actions',
      title: t(locale, 'quickActions.title'),
      description: t(locale, 'quickActions.description'),
      spotlight: '[data-tour="quick-actions"]',
      icon: Zap,
    },
  ];

  if (isAdmin) {
    steps.push({
      id: 'admin',
      title: t(locale, 'admin.title'),
      description: t(locale, 'admin.description'),
      spotlight: '[data-tour="admin-link"]',
      icon: Settings,
    });
  }

  steps.push({
    id: 'complete',
    title: t(locale, 'complete.title'),
    description: t(locale, 'complete.description'),
    icon: Sparkles,
  });

  return steps;
};

/**
 * HotspotTour - Alternative onboarding mode that highlights elements on the live page
 *
 * Instead of a modal wizard, this highlights actual UI elements and shows
 * tooltips directly on the page as the user navigates.
 */
const HotspotTour = ({
  selectedGoal,
  onSkip,
  onComplete,
  locale = 'en',
}) => {
  const { user } = useContext(UserContext);
  const userRoles = useMemo(() => (user?.role_slugs || []).map(s => String(s).toLowerCase()), [user]);

  // Get steps based on selected goal or use defaults
  const steps = useMemo(() => {
    if (selectedGoal && USER_GOALS[selectedGoal]?.steps) {
      const goalSteps = USER_GOALS[selectedGoal].steps;
      const allSteps = getDefaultSteps(locale, userRoles);
      return allSteps.filter(s => goalSteps.includes(s.id));
    }
    return getDefaultSteps(locale, userRoles);
  }, [selectedGoal, locale, userRoles]);

  const [currentStep, setCurrentStep] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleToggleAudio = useCallback(() => {
    setAudioEnabled(prev => !prev);
  }, []);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  const [animating, setAnimating] = useState(false);
  const audioRef = useRef(null);

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const media = STEP_MEDIA[step?.id];
  const helpArticle = HELP_ARTICLES[step?.id];

  // Remaining time estimate
  const remainingSteps = steps.slice(currentStep);
  const remainingTime = getEstimatedTime(remainingSteps.map(s => s.id));

  // Find and position around target element
  const updatePosition = useCallback(() => {
    if (!step?.spotlight) {
      setTargetRect(null);
      return;
    }

    const target = document.querySelector(step.spotlight);
    if (!target) {
      // Element not found, try again after a short delay (page might still be loading)
      const retryTimer = setTimeout(updatePosition, 500);
      return () => clearTimeout(retryTimer);
    }

    const rect = target.getBoundingClientRect();
    const padding = 8;

    setTargetRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Calculate tooltip position
    const tooltipWidth = 360;
    const tooltipHeight = 280;
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let placement = 'bottom';
    let top = rect.bottom + gap;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    // Check if tooltip fits below
    if (top + tooltipHeight > viewportHeight - 20) {
      // Try above
      if (rect.top - tooltipHeight - gap > 20) {
        placement = 'top';
        top = rect.top - tooltipHeight - gap;
      } else {
        // Try right
        if (rect.right + tooltipWidth + gap < viewportWidth - 20) {
          placement = 'right';
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + gap;
        } else {
          // Try left
          placement = 'left';
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - gap;
        }
      }
    }

    // Keep in viewport
    if (left < 20) left = 20;
    if (left + tooltipWidth > viewportWidth - 20) left = viewportWidth - tooltipWidth - 20;
    if (top < 20) top = 20;
    if (top + tooltipHeight > viewportHeight - 20) top = viewportHeight - tooltipHeight - 20;

    setTooltipPosition({ top, left, placement });
  }, [step]);

  // Update position on mount and when step changes
  useEffect(() => {
    setAnimating(true);
    const timer = setTimeout(() => setAnimating(false), 300);

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition, currentStep]);

  // Handle audio playback
  useEffect(() => {
    if (audioEnabled && media?.audio?.[locale]) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(media.audio[locale]);
      audioRef.current.play().catch(() => {
        // Audio playback failed (probably not available or blocked)
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [currentStep, audioEnabled, locale, media]);

  // Scroll target into view
  useEffect(() => {
    if (step?.spotlight) {
      const target = document.querySelector(step.spotlight);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [step]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (isLastStep) {
          onComplete?.();
        } else {
          handleNext();
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!isFirstStep) {
          handlePrev();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onSkip?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFirstStep, isLastStep, handleNext, handlePrev, onSkip, onComplete]);

  if (!step) return null;

  const arrowStyles = {
    top: 'left-1/2 -translate-x-1/2 bottom-0 translate-y-full border-t-white dark:border-t-slate-800',
    bottom: 'left-1/2 -translate-x-1/2 top-0 -translate-y-full border-b-white dark:border-b-slate-800',
    left: 'top-1/2 -translate-y-1/2 right-0 translate-x-full border-l-white dark:border-l-slate-800',
    right: 'top-1/2 -translate-y-1/2 left-0 -translate-x-full border-r-white dark:border-r-slate-800',
  };

  const content = (
    <>
      {/* Overlay with spotlight cutout */}
      {targetRect && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <svg className="absolute inset-0 w-full h-full">
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect
                  x={targetRect.left}
                  y={targetRect.top}
                  width={targetRect.width}
                  height={targetRect.height}
                  rx="8"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.6)"
              mask="url(#spotlight-mask)"
            />
          </svg>

          {/* Pulse ring around target */}
          <div
            className="absolute border-2 border-blue-500 rounded-lg animate-pulse"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
            }}
          />
        </div>
      )}

      {/* Tooltip */}
      <div
        className={`fixed z-[101] w-[360px] transition-all duration-300 ${
          animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-8 border-transparent ${arrowStyles[tooltipPosition.placement]}`}
          />

          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium bg-white/20 px-2 py-0.5 rounded-full">
                  {currentStep + 1} of {steps.length}
                </span>
                <span className="text-xs text-white/70">
                  ~{formatTime(remainingTime)} left
                </span>
              </div>
              <div className="flex items-center gap-1">
                {media?.audio && (
                  <button
                    onClick={handleToggleAudio}
                    className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                    title={audioEnabled ? 'Mute' : 'Enable audio'}
                  >
                    {audioEnabled ? (
                      <Volume2 className="w-4 h-4" />
                    ) : (
                      <VolumeX className="w-4 h-4" />
                    )}
                  </button>
                )}
                <button
                  onClick={onSkip}
                  className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                  title="Skip tour"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <h3 className="text-lg font-bold mt-2">{step.title}</h3>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-slate-200 dark:bg-slate-700">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Media preview */}
            {(media?.gif || media?.video) && (
              <div className="mb-4 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                {media.video ? (
                  <video
                    src={media.video}
                    className="w-full h-32 object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onError={(e) => {
                      // Fallback to GIF if video fails
                      e.target.style.display = 'none';
                    }}
                  />
                ) : media.gif ? (
                  <img
                    src={media.gif}
                    alt={step.title}
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : null}
              </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-400">
              {step.description}
            </p>

            {/* Try it button for interactive steps */}
            {step.interactive && (
              <button
                onClick={() => {
                  if (step.interactiveAction === 'triggerSearch') {
                    const isMac = navigator.platform.includes('Mac');
                    const event = new KeyboardEvent('keydown', {
                      key: 'k',
                      metaKey: isMac,
                      ctrlKey: !isMac,
                      bubbles: true,
                    });
                    document.dispatchEvent(event);
                  }
                }}
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                <Play className="w-4 h-4" />
                {t(locale, 'common.tryIt')}
              </button>
            )}

            {/* Help article link */}
            {helpArticle && (
              <a
                href={helpArticle.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <HelpCircle className="w-3 h-3" />
                {helpArticle.title}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                isFirstStep
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              {t(locale, 'common.back')}
            </button>

            <button
              onClick={isLastStep ? onComplete : handleNext}
              className="inline-flex items-center gap-1 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              {isLastStep ? t(locale, 'common.getStarted') : t(locale, 'common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default HotspotTour;
